import React, { useState } from "react";
import {
    Box,
    Stack,
    Button,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import moment from "moment-timezone";
import decimalToFraction from "../../../utilities/DecimalToFraction";
import DrawingDialog from "../../DrawingDialog";

// Panel Order Dialog Component
function PanelOrderDialog({
    open,
    onClose,
    order,
    user,
    onMarkChecked,
    onMarkUnchecked,
}) {
    const [openDrawingDialog, setOpenDrawingDialog] = useState(false);
    const [selectedDrawing, setSelectedDrawing] = useState(null);

    if (!order) return null;

    const dueDate = order.due ? moment(order.due).format("MM/DD/YY") : "—";

    // Determine which project target is being used
    const projectLike = order.project || order.service || order.hse || {};
    const projectLabel =
        projectLike.number && projectLike.name
            ? `${projectLike.number} ${projectLike.name}`
            : projectLike.name || projectLike.number || "—";

    const targetType = order.project
        ? "Project"
        : order.service
        ? "Service"
        : order.hse
        ? "HSE"
        : "Unknown";

    const assigneeName = order.assignee
        ? `${order.assignee.first_name || ""} ${order.assignee.last_name ||
              ""}`.trim()
        : "—";
    const drawings = Array.isArray(order.drawings) ? order.drawings : [];

    // Check if user has MANAGEMENT group access
    const isManagement =
        user &&
        user.groups &&
        user.groups.some((group) => group.name === "MANAGEMENT");

    // Checked information
    const checkedDateDisplay = order.checked
        ? moment(order.checked).format("MM/DD/YY")
        : "—";
    const checkedByDisplay = order.checked_by
        ? order.checked_by.full_name ||
          `${order.checked_by.first_name || ""} ${order.checked_by.last_name ||
              ""}`.trim() ||
          "—"
        : "—";

    const handleOpenDrawing = (d) => {
        const url =
            d.document ||
            d.file_url ||
            d.file ||
            d.url ||
            d.link ||
            d.href ||
            "";
        if (!url) return;
        setSelectedDrawing({ ...d, document: url });
        setOpenDrawingDialog(true);
    };

    // Calculate totals
    const totalPanels = (order.items || []).reduce(
        (sum, item) => sum + (Number(item.quantity) || 0),
        0
    );

    return (
        <Dialog fullScreen open={open} disableEscapeKeyDown>
            <DialogTitle>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={{ xs: 2, md: 0 }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    sx={{ position: "relative", pr: { xs: 5, md: 0 } }}
                >
                    <Stack
                        direction="column"
                        spacing={0.5}
                        sx={{ minWidth: 0, flex: 1 }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                minWidth: 0,
                                wordBreak: "break-word",
                                overflow: "hidden",
                                fontWeight: 700,
                            }}
                        >
                            {projectLabel}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 0, wordBreak: "break-word" }}
                        >
                            Work Order: {order.work_order_number || "—"}
                        </Typography>
                        <Box
                            sx={{
                                display: { xs: "flex", md: "none" },
                                gap: 1,
                                mt: 1,
                            }}
                        >
                            {isManagement &&
                                (order.checked ? (
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        startIcon={<CancelIcon />}
                                        onClick={() => {
                                            if (onMarkUnchecked) {
                                                onMarkUnchecked(order.id);
                                            }
                                        }}
                                        sx={{
                                            fontSize: {
                                                xs: "0.75rem",
                                                md: "0.875rem",
                                            },
                                            px: { xs: 1, md: 2 },
                                            py: { xs: 0.5, md: 1 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "none",
                                                    sm: "inline",
                                                },
                                            }}
                                        >
                                            Mark as Unchecked
                                        </Box>
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "inline",
                                                    sm: "none",
                                                },
                                            }}
                                        >
                                            Uncheck
                                        </Box>
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<VerifiedIcon />}
                                        onClick={() => {
                                            if (onMarkChecked) {
                                                onMarkChecked(order.id);
                                            }
                                        }}
                                        sx={{
                                            fontSize: {
                                                xs: "0.75rem",
                                                md: "0.875rem",
                                            },
                                            px: { xs: 1, md: 2 },
                                            py: { xs: 0.5, md: 1 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "none",
                                                    sm: "inline",
                                                },
                                            }}
                                        >
                                            Mark as Checked
                                        </Box>
                                        <Box
                                            sx={{
                                                display: {
                                                    xs: "inline",
                                                    sm: "none",
                                                },
                                            }}
                                        >
                                            Check
                                        </Box>
                                    </Button>
                                ))}
                        </Box>
                    </Stack>
                    <Box
                        sx={{
                            display: { xs: "none", md: "flex" },
                            gap: 1,
                            alignItems: "center",
                        }}
                    >
                        {isManagement &&
                            (order.checked ? (
                                <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<CancelIcon />}
                                    onClick={() => {
                                        if (onMarkUnchecked) {
                                            onMarkUnchecked(order.id);
                                        }
                                    }}
                                    sx={{
                                        fontSize: {
                                            xs: "0.75rem",
                                            md: "0.875rem",
                                        },
                                        px: { xs: 1, md: 2 },
                                        py: { xs: 0.5, md: 1 },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "none",
                                                sm: "inline",
                                            },
                                        }}
                                    >
                                        Mark as Unchecked
                                    </Box>
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "inline",
                                                sm: "none",
                                            },
                                        }}
                                    >
                                        Uncheck
                                    </Box>
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<VerifiedIcon />}
                                    onClick={() => {
                                        if (onMarkChecked) {
                                            onMarkChecked(order.id);
                                        }
                                    }}
                                    sx={{
                                        fontSize: {
                                            xs: "0.75rem",
                                            md: "0.875rem",
                                        },
                                        px: { xs: 1, md: 2 },
                                        py: { xs: 0.5, md: 1 },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "none",
                                                sm: "inline",
                                            },
                                        }}
                                    >
                                        Mark as Checked
                                    </Box>
                                    <Box
                                        sx={{
                                            display: {
                                                xs: "inline",
                                                sm: "none",
                                            },
                                        }}
                                    >
                                        Check
                                    </Box>
                                </Button>
                            ))}
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: { xs: "absolute", md: "static" },
                            top: { xs: -8, md: 0 },
                            right: { xs: -8, md: 0 },
                            color: "inherit",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ m: 2 }}>
                <Stack spacing={2}>
                    <Stack
                        direction="row"
                        spacing={3}
                        divider={<Divider orientation="vertical" flexItem />}
                    >
                        <Typography>
                            <b>Due:</b> {dueDate}
                        </Typography>
                        <Typography>
                            <b>Assigned To:</b> {assigneeName || "—"}
                        </Typography>
                    </Stack>
                    {order.checked ? (
                        <>
                            <Divider
                                sx={{
                                    borderColor: "success.main",
                                    borderWidth: 1,
                                }}
                            />
                            <Stack
                                direction="row"
                                spacing={3}
                                divider={
                                    <Divider orientation="vertical" flexItem />
                                }
                            >
                                <Typography>
                                    <b>Checked:</b> {checkedDateDisplay}{" "}
                                </Typography>
                                <Typography>
                                    <b>Checked By:</b> {checkedByDisplay}
                                </Typography>
                            </Stack>
                        </>
                    ) : null}

                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Typography sx={{ textDecoration: "underline" }}>
                        Panel Specifications
                    </Typography>
                    <Stack spacing={1}>
                        <Typography>
                            <b>Total Panels:</b> {totalPanels}
                        </Typography>
                    </Stack>

                    {/* Unified table layout for both single and multiple items */}
                    {(order.items || []).length > 0 && (
                        <Box
                            sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 2,
                            }}
                        >
                            {/* Header */}
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "5ch 2fr 1fr",
                                    gap: 1,
                                    p: 1,
                                    bgcolor: "primary.main",
                                    color: "primary.contrastText",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        textAlign: "left",
                                        fontSize: { xs: "0.75rem", sm: "1rem" },
                                    }}
                                >
                                    Qty
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        textAlign: "left",
                                        fontSize: { xs: "0.75rem", sm: "1rem" },
                                    }}
                                >
                                    Dimensions
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        textAlign: "left",
                                        fontSize: { xs: "0.75rem", sm: "1rem" },
                                    }}
                                >
                                    Notes
                                </Typography>
                            </Box>

                            {/* Rows */}
                            {(order.items || []).map((item, index) => {
                                const width = item.width;
                                const height = item.height;
                                const qty = item.quantity || 0;
                                const notes = item.notes || "—";

                                return (
                                    <Box
                                        key={item.id || index}
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "5ch 2fr 1fr",
                                            gap: 1,
                                            p: 1,
                                            bgcolor:
                                                index % 2 === 0
                                                    ? "background.default"
                                                    : "background.paper",
                                            borderTop: index > 0 ? 1 : 0,
                                            borderColor: "divider",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                textAlign: "left",
                                                fontWeight: "bold",
                                                fontSize: {
                                                    xs: "0.75rem",
                                                    sm: "1rem",
                                                },
                                            }}
                                        >
                                            {qty}
                                        </Typography>
                                        <Typography
                                            noWrap
                                            sx={{
                                                textAlign: "left",
                                                fontSize: {
                                                    xs: "0.7rem",
                                                    sm: "1rem",
                                                },
                                            }}
                                        >
                                            {width && height
                                                ? `${decimalToFraction(
                                                      width
                                                  )}" x ${decimalToFraction(
                                                      height
                                                  )}"`
                                                : width
                                                ? `${decimalToFraction(width)}"`
                                                : height
                                                ? `${decimalToFraction(
                                                      height
                                                  )}"`
                                                : "—"}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                textAlign: "left",
                                                fontSize: {
                                                    xs: "0.75rem",
                                                    sm: "0.875rem",
                                                },
                                                wordBreak: "break-word",
                                                whiteSpace: "normal",
                                            }}
                                        >
                                            {notes}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    {(order.list || []).length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography
                                sx={{
                                    fontWeight: "bold",
                                    color: "primary.main",
                                    fontSize: "1.1rem",
                                }}
                            >
                                Total Panels: {totalPanels}
                            </Typography>
                        </>
                    )}

                    {order.comments && (
                        <>
                            <Divider
                                sx={{
                                    borderColor: "success.main",
                                    borderWidth: 1,
                                }}
                            />
                            <Typography sx={{ textDecoration: "underline" }}>
                                Comments
                            </Typography>

                            <Typography fontWeight="bold" whiteSpace="pre-wrap">
                                {order.comments}
                            </Typography>
                        </>
                    )}

                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Typography sx={{ textDecoration: "underline" }}>
                        Drawings
                    </Typography>
                    <Stack spacing={1}>
                        <div>
                            <Divider sx={{ mt: 2 }} />
                            {drawings.length > 0 ? (
                                <Box textAlign="center">
                                    <List sx={{ mt: 0, pt: 0 }}>
                                        {drawings.map((d) => (
                                            <div key={d.id}>
                                                <ListItem disablePadding>
                                                    <ListItemButton
                                                        onClick={() =>
                                                            handleOpenDrawing(d)
                                                        }
                                                    >
                                                        <ListItemIcon>
                                                            <ArchitectureIcon color="secondary" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={d.title}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                                <Divider />
                                            </div>
                                        ))}
                                    </List>
                                </Box>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 1 }}
                                >
                                    No drawings available
                                </Typography>
                            )}
                        </div>
                    </Stack>
                    <Divider
                        sx={{ borderColor: "success.main", borderWidth: 1 }}
                    />
                    <Stack spacing={1}>
                        <Typography>
                            <b>Created Date:</b>{" "}
                            {order.created
                                ? moment(order.created).format("MM/DD/YY")
                                : "—"}
                        </Typography>
                        <Typography>
                            <b>Created By:</b>{" "}
                            {order.created_by
                                ? `${order.created_by.first_name || ""} ${order
                                      .created_by.last_name || ""}`.trim()
                                : "—"}
                        </Typography>
                        <Typography>
                            <b>Last Updated:</b>{" "}
                            {order.updated
                                ? moment(order.updated).format(
                                      "MM/DD/YY h:mm A"
                                  )
                                : "—"}
                        </Typography>
                        {order.is_complete && (
                            <Typography>
                                <b>Completed At:</b>{" "}
                                {order.completed
                                    ? moment(order.completed).format(
                                          "MM/DD/YY h:mm A"
                                      )
                                    : "—"}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>

            {selectedDrawing && (
                <DrawingDialog
                    openDrawingDialog={openDrawingDialog}
                    setOpenDrawingDialog={setOpenDrawingDialog}
                    drawing={selectedDrawing}
                />
            )}
        </Dialog>
    );
}

export default PanelOrderDialog;
